import { useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/ui/utils/tailwind';
import { tailwindUtils } from '@/shared/ui/utils/tailwind';
import { CardListSkeleton, Text, TextInput, UnstyledButton } from '@/shared/ui';
import { AdaptiveFormShell } from '@/shared/ui/components/form';

export interface EntityPickerProps<T> {
  agencyId: string;
  onSelect: (item: T) => void;
  onClose: () => void;
  title: string;
  /** Fetches the candidate items for the given agency (already unwrapped from the paginated CRUD response). */
  fetchItems: (agencyId: string) => Promise<T[]>;
  getKey: (item: T) => string;
  matchesSearch: (item: T, search: string) => boolean;
  renderPrimary: (item: T) => ReactNode;
  renderSecondary: (item: T) => ReactNode;
}

/**
 * Modal picker that lists an agency's entities (with search) and lets the
 * user select one as a partner-search reference. Shared by the client and
 * property pickers — they only differ in how items are fetched, matched,
 * keyed and rendered.
 *
 * @param props - Agency id, fetch/match/render callbacks and display copy.
 * @returns The entity selection modal.
 */
export function EntityPicker<T>({
  agencyId,
  onSelect,
  onClose,
  title,
  fetchItems,
  getKey,
  matchesSearch,
  renderPrimary,
  renderSecondary,
}: EntityPickerProps<T>) {
  const { t } = useTranslation('features/partnerships');
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchItems(agencyId)
      .then((result) => {
        if (!cancelled) setItems(result);
      })
      .catch(() => { /* handled by empty list */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId]);

  const filtered = search.trim() ? items.filter((item) => matchesSearch(item, search)) : items;

  return (
    <AdaptiveFormShell open={true} onClose={onClose} title={title} density="compact">
      <div className="flex flex-col gap-(--space-3)">
        <TextInput
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('wizard.search_placeholder')}
        />
        <div className="overflow-y-auto max-h-[50vh] flex flex-col gap-(--space-2)">
          {loading && <CardListSkeleton count={4} />}
          {!loading && filtered.length === 0 && (
            <Text className="text-center text-sm text-[var(--text-secondary)]">
              {t('wizard.no_items')}
            </Text>
          )}
          {!loading && filtered.map((item) => (
            <UnstyledButton
              key={getKey(item)}
              type="button"
              onClick={() => onSelect(item)}
              className={cn(
                tailwindUtils.card,
                'w-full cursor-pointer p-[var(--space-3)] text-left',
                'hover:border-[var(--seed-base)]',
              )}
            >
              <Text component="span" className="text-sm font-medium">{renderPrimary(item)}</Text>
              <Text component="span" className="block text-xs text-[var(--text-secondary)]">
                {renderSecondary(item)}
              </Text>
            </UnstyledButton>
          ))}
        </div>
      </div>
    </AdaptiveFormShell>
  );
}

export default EntityPicker;
